import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'

/**
 * Die hinterlegten Dateien eines Datensatzes: anzeigen, hinzufügen, entfernen.
 *
 * Dieselbe Liste erscheint an mehreren Stellen — am Kursblatt in der Bibliothek, beim
 * einzelnen Kurs und beim Betrieb. Sie einmal zu bauen ist nicht nur weniger Code: Wer
 * Dateien an zwei Orten unterschiedlich bedient bekommt, traut keinem von beiden.
 */

export type Bezugsart = 'KURSBLATTDATEI' | 'KURS' | 'MANDANT'

export interface DokumentAntwort {
  id: number
  bezugsart: string
  bezugId: number | null
  art: string
  dateiname: string
  mimetyp: string | null
  groesseBytes: number
  speicherart: string
  savename: string
  pfad: string
  abrufbar: boolean
  bemerkung: string | null
  hochgeladenAm: string
}

const ART_TEXT: Record<string, string> = {
  KURSBLATT: 'Kursblatt',
  FAHRPLAN: 'Fahrplan',
  UMLAUFPLAN: 'Umlaufplan',
  SONSTIGES: 'Sonstiges',
}

const SPEICHER_TEXT: Record<string, string> = {
  LOKAL: 'Server',
  S3: 'S3',
  DATENBANK: 'Datenbank',
}

/** „842 KB" statt „862 208 Bytes" — Dateigrößen liest niemand in Bytes. */
export function alsGroesse(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

interface Eigenschaften {
  bezugsart: Bezugsart
  bezugId?: number | null
  /** Vorgewählte Art beim Hochladen; ohne Angabe rät der Server anhand der Endung. */
  art?: string
  titel?: string
  hinweis?: string
  /** Wird nach jeder Änderung gerufen — die umgebende Seite kann neu laden. */
  beiAenderung?: () => void
  kompakt?: boolean
}

export function Dokumente({
  bezugsart,
  bezugId,
  art,
  titel,
  hinweis,
  beiAenderung,
  kompakt = false,
}: Eigenschaften) {
  const { hatRolle } = useAuth()
  const [dokumente, setDokumente] = useState<DokumentAntwort[]>([])
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const dateiFeld = useRef<HTMLInputElement>(null)

  const darfBearbeiten = hatRolle('ADMIN', 'DISPONENT')

  async function laden() {
    setLaedt(true)
    try {
      const abfrage = new URLSearchParams({ bezugsart })
      if (bezugId != null) abfrage.set('bezugId', String(bezugId))
      setDokumente(await api.get<DokumentAntwort[]>(`/api/dokumente?${abfrage}`))
      setFehler(null)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen')
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    void laden()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bezugsart, bezugId])

  async function hochladen(dateiliste: FileList | null) {
    if (!dateiliste || dateiliste.length === 0) return
    setLaeuft(true)
    setFehler(null)
    const fehlgeschlagen: string[] = []

    for (const datei of Array.from(dateiliste)) {
      try {
        const abfrage = new URLSearchParams({ bezugsart })
        if (bezugId != null) abfrage.set('bezugId', String(bezugId))
        if (art) abfrage.set('art', art)
        await api.hochladen<DokumentAntwort>(`/api/dokumente?${abfrage}`, datei)
      } catch (ex) {
        fehlgeschlagen.push(`${datei.name}: ${ex instanceof Error ? ex.message : 'Fehler'}`)
      }
    }

    if (fehlgeschlagen.length > 0) setFehler(fehlgeschlagen.join(' · '))
    if (dateiFeld.current) dateiFeld.current.value = ''
    setLaeuft(false)
    await laden()
    beiAenderung?.()
  }

  async function entfernen(dokument: DokumentAntwort) {
    if (!confirm(`„${dokument.dateiname}" entfernen?`)) return
    try {
      await api.loeschen(`/api/dokumente/${dokument.id}`)
      await laden()
      beiAenderung?.()
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Löschen fehlgeschlagen')
    }
  }

  async function holen(dokument: DokumentAntwort) {
    try {
      await api.herunterladen(`/api/dokumente/${dokument.id}/datei`, dokument.dateiname)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Herunterladen fehlgeschlagen')
    }
  }

  return (
    <div className={kompakt ? undefined : 'karte abschnitt'}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 12,
          marginBottom: 10,
        }}
      >
        <div>
          {titel && <h3 style={{ margin: 0 }}>{titel}</h3>}
          {hinweis && (
            <p style={{ margin: '4px 0 0', color: 'var(--grau-500)', fontSize: '0.85rem' }}>
              {hinweis}
            </p>
          )}
        </div>
        {darfBearbeiten && (
          <div style={{ whiteSpace: 'nowrap' }}>
            <input
              ref={dateiFeld}
              type="file"
              multiple
              accept=".pdf,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.txt,.docx"
              style={{ display: 'none' }}
              onChange={(e) => void hochladen(e.target.files)}
            />
            <button
              className="knopf-schlicht"
              disabled={laeuft}
              onClick={() => dateiFeld.current?.click()}
            >
              {laeuft ? 'Wird übertragen …' : 'Datei hinzufügen'}
            </button>
          </div>
        )}
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      {laedt ? (
        <div className="laden">Wird geladen …</div>
      ) : dokumente.length === 0 ? (
        <div className="leer" style={{ padding: '12px 0' }}>
          Nichts hinterlegt.
          {darfBearbeiten && ' Fahrpläne, Kursblätter und Schriftverkehr gehören hierher.'}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Datei</th>
              <th>Art</th>
              <th>Größe</th>
              <th>Ablage</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {dokumente.map((d) => (
              <tr key={d.id}>
                <td>
                  <strong>{d.dateiname}</strong>
                  {d.bemerkung && (
                    <div style={{ color: 'var(--grau-500)', fontSize: '0.8rem' }}>{d.bemerkung}</div>
                  )}
                  {!d.abrufbar && (
                    <div style={{ color: 'var(--rot-500)', fontSize: '0.8rem' }}>
                      Die Datei liegt nicht mehr in der Ablage.
                    </div>
                  )}
                </td>
                <td>{ART_TEXT[d.art] ?? d.art}</td>
                <td className="zahl">{alsGroesse(d.groesseBytes)}</td>
                <td>
                  <span className="etikett grau">
                    {SPEICHER_TEXT[d.speicherart] ?? d.speicherart}
                  </span>
                  {/* Der Name auf dem Speicher. Wer im Ablageverzeichnis steht, sieht dort
                      nur diesen — die Zuordnung zum Originalnamen gehoert sichtbar hierher. */}
                  <div
                    title={`${d.pfad}/${d.savename}`}
                    style={{
                      color: 'var(--grau-500)',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      marginTop: 2,
                    }}
                  >
                    {d.savename}
                  </div>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    className="knopf-schlicht"
                    style={{ padding: '5px 12px' }}
                    onClick={() => void holen(d)}
                  >
                    Herunterladen
                  </button>{' '}
                  {darfBearbeiten && (
                    <button
                      className="knopf-schlicht"
                      style={{ padding: '5px 12px', color: 'var(--rot-500)' }}
                      onClick={() => void entfernen(d)}
                    >
                      Entfernen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
